import * as React from 'react';
import { Paper, makeStyles } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';

import {
  ButtonAnchor,
  Anchor,
  Flex,
  Box,
  H1,
  H3
} from '@urbaninfrastructure/react-ui-kit';
import { useUpdateCurrentUserMutation } from '../../../lib/core/mutations';
import { FormattedCurrency } from '../../../components/FormattedCurrency';
import { Loading, i18nMessages, Link } from '../../../components';
import { useCurrentUser } from '../../../lib/core/useCurrentUser';
import { TopUpWalletResultStep } from './Step/TopUpWalletResult';
import { CurrentUser, receiptsQuery } from '../../../core-types';
import { State, Wizard } from '../../../components/Wizard';
import { EnableWalletStep } from './Step/EnableWallet';
import { TopUpWalletStep } from './Step/TopUpWallet';
import { RECEIPTS_QUERY } from '../Receipts';
import formatDate from 'date-fns/format';
import { parseISO } from 'date-fns';
import { wallet } from './messages';
import dayjs from 'dayjs';

import { useToasts } from '../../../components/Toasts';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  actionsContainer: {
    marginBottom: theme.spacing(2)
  },
  resetContainer: {
    padding: theme.spacing(3)
  }
}));

const Card = styled(Box)`
  box-sizing: border-box;
  padding: 32px;
  margin-bottom: 64px;
  height: 100%;
  border-radius: 10px;
  text-align: center;
  flex-direction: column;
  min-width: 450px;
  display: flex;
  color: #fff;
`;

const StyledButtonAnchor = styled(ButtonAnchor)`
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    backgroundcolor: ${({ theme }) => theme.colors.white};
    filter: brightness(100%);
  }
`;

function WalletBalance({ handleTopUpClick }: { handleTopUpClick: () => void }) {
  const classes = useStyles();
  const { addToast } = useToasts();
  const { currentUser } = useCurrentUser();
  const { data, loading } = useQuery<receiptsQuery>(RECEIPTS_QUERY);

  const [
    updateCurrentUser,
    { loading: updateCurrentUserLoading, error }
  ] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      addToast({
        text: <FormattedMessage {...wallet.wallet_disabled} />,
        type: 'success'
      });
    },
    onError: () => {
      addToast({
        text: error?.message || (
          <FormattedMessage {...wallet.generic_wallet_error} />
        ),
        type: 'error'
      });
    }
  });

  const handleDisableWalletClick = () => {
    updateCurrentUser({
      variables: {
        input: {
          walletEnabled: false
        }
      }
    });
  };

  const walletTopUpDates =
    data?.currentUser?.completedOrders
      ?.map(
        order =>
          (order && order.type === 'wallet_deposit' && order.completedAt) ||
          null
      )
      .filter(Boolean) || [];

  const walletLatestTopUpDate =
    walletTopUpDates
      ?.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? 1 : -1))
      .filter(Boolean)[0] || null;

  if (loading || updateCurrentUserLoading) {
    return <Loading />;
  }

  return (
    <Box>
      <H1 textAlign="center">
        <FormattedMessage {...wallet.wallet_title} />
      </H1>
      <Paper square elevation={0} className={classes.resetContainer}>
        <Box display={'flex'} justifyContent={'center'}>
          <Card bg={'primary'}>
            <H3 mb={6} textAlign="center">
              <FormattedCurrency
                value={currentUser?.wallet?.balance || 0}
                precision={2}
              />
            </H3>
            <Box mb={6}>
              <FormattedMessage
                {...wallet.wallet_last_topup}
                values={{
                  0: walletLatestTopUpDate
                    ? formatDate(parseISO(walletLatestTopUpDate), 'd MMM yyyy')
                    : ''
                }}
              />
            </Box>
            <Box>
              <StyledButtonAnchor
                shape="cool"
                variant="primary"
                bg={'white'}
                color={'primary'}
                mb="xs"
                onClick={() => handleTopUpClick()}
              >
                <FormattedMessage {...wallet.wallet_topup} />
              </StyledButtonAnchor>
            </Box>
            <Box>
              <ButtonAnchor
                shape="cool"
                variant="link"
                color="primaryContrast"
                onClick={() => handleDisableWalletClick()}
                disabled={(currentUser?.wallet?.balance || 0) < 0}
              >
                <FormattedMessage {...wallet.disable_wallet} />
              </ButtonAnchor>
            </Box>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
}

type StepId =
  | 'TopUpWallet'
  | 'TopUpWalletResult'
  | 'WalletBalance'
  | 'EnableWallet';

export const Wallet = ({
  initialStepId = 'TopUpWallet',
  currentUser,
  onChange
}: {
  initialStepId?: StepId;
  currentUser: CurrentUser;
  onChange?: (props: State) => void;
}) => {
  const isUsersWalletEnabled = currentUser?.wallet?.enabled;
  const steps: Array<{ id: StepId; component: React.ReactElement }> = [
    {
      id: 'TopUpWallet',
      component: <TopUpWalletStep currentUser={currentUser} />
    }
  ];
  const initialStepIndex = steps.findIndex(step => step.id === initialStepId);
  const topUpStepIndex = steps.findIndex(step => step.id === 'TopUpWallet');
  const [stepIndex, setStepIndex] = React.useState<number>(initialStepIndex);

  const handleTopUpClick = () => {
    if (isUsersWalletEnabled) {
      setStepIndex(topUpStepIndex + 1);
    } else {
      setStepIndex(topUpStepIndex);
    }
  };

  const handleContinuelick = () => {
    setStepIndex(0);
  };

  steps.push({
    id: 'TopUpWalletResult',
    component: (
      <TopUpWalletResultStep
        currentUser={currentUser}
        handleContinuelick={handleContinuelick}
      />
    )
  });

  if (isUsersWalletEnabled) {
    steps.unshift({
      id: 'WalletBalance',
      component: <WalletBalance handleTopUpClick={handleTopUpClick} />
    });
  } else {
    steps.unshift({
      id: 'EnableWallet',
      component: <EnableWalletStep currentUser={currentUser} />
    });
    steps.push({
      id: 'WalletBalance',
      component: <WalletBalance handleTopUpClick={handleTopUpClick} />
    });
  }

  return (
    <>
      <Box>
        <Flex
          column
          mb={6}
          mt={6}
          alignItems="center"
          position="absolute"
          width="100%"
          top="65px"
          zIndex={2}
        >
          <Link route="profile" passHref>
            <Anchor textDecoration="none" typoStyle="xxs">
              / <FormattedMessage {...i18nMessages.profile} />
            </Anchor>
          </Link>
        </Flex>
      </Box>
      <Wizard
        stepIndex={stepIndex}
        stepCount={steps.length}
        onStepChange={props => {
          onChange && onChange(props);
          if (props.nextStepIndex !== null) {
            setStepIndex(props.nextStepIndex);
          }
        }}
      >
        {steps[stepIndex].component}
      </Wizard>
    </>
  );
};
